import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import {
  ConfigurationLevel,
  configurationQueryKey,
  type RevertConfigurationResponse,
} from '~/settings/components/ConfigurationsPage/helpers/configurationApi';

import type { ConfigRecord } from '~/settings/components/ConfigurationsPage/types';

interface UseConfigurationFormParams {
  nodeId: string | null;
  nodeLevel?: ConfigurationLevel;
  configRecord: ConfigRecord | undefined;
  instanceId?: string;
  region?: string;
  accountId?: string;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onRevertField: (nodeId: string, fieldName: string) => Promise<RevertConfigurationResponse>;
  onRevertAll: (nodeId: string) => Promise<RevertConfigurationResponse>;
}

export function useConfigurationForm({
  nodeId,
  nodeLevel,
  configRecord,
  instanceId,
  region,
  accountId,
  onHasChangesChange,
  onRevertField,
  onRevertAll,
}: UseConfigurationFormParams) {
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);
  const [revertingField, setRevertingField] = useState<string | null>(null);
  const [isRevertingAll, setIsRevertingAll] = useState(false);

  const prevNodeIdRef = useRef<string | null>(null);
  const prevConfigRecordRef = useRef<ConfigRecord | undefined | null>(null);
  const pendingRevertFieldRef = useRef<string | null>(null);
  const pendingRevertAllRef = useRef<boolean>(false);

  const {
    register,
    control,
    watch,
    setValue,
    setError,
    reset,
    handleSubmit,
    getValues,
    formState: { errors, isValid },
  } = useForm<Record<string, string | number>>({ mode: 'all' });

  const fields = watch();

  const normalizeValue = (value: string | number | undefined | null): string => {
    if (value === undefined || value === null) {
      return '';
    }
    const str = String(value).trim();
    return str === '' ? '' : str;
  };

  // Initialize form when config loads
  useEffect(() => {
    if (!configRecord) {
      reset({});
      setHasChanges(false);
      onHasChangesChange?.(false);
      prevConfigRecordRef.current = null;
      return;
    }

    const nodeIdChanged = prevNodeIdRef.current !== nodeId;
    const configRecordFirstLoad = prevConfigRecordRef.current === null && configRecord !== null;
    const isRevertAll = pendingRevertAllRef.current;

    if (nodeIdChanged || configRecordFirstLoad || isRevertAll) {
      if (nodeIdChanged) {
        prevNodeIdRef.current = nodeId;
      }
      prevConfigRecordRef.current = configRecord;

      const initialFields: Record<string, string | number> = {};
      configRecord.fields.forEach((field) => {
        initialFields[field.name] = field.currentValue;
      });
      reset(initialFields);
      setHasChanges(false);
      onHasChangesChange?.(false);

      if (isRevertAll) {
        pendingRevertAllRef.current = false;
        setIsRevertingAll(false);
      }
    } else if (pendingRevertFieldRef.current) {
      const fieldName = pendingRevertFieldRef.current;
      const field = configRecord.fields.find((f) => f.name === fieldName);
      if (field) {
        setValue(field.name, field.currentValue);
      }
      pendingRevertFieldRef.current = null;
      prevConfigRecordRef.current = configRecord;
    } else {
      prevConfigRecordRef.current = configRecord;
    }
  }, [configRecord, reset, onHasChangesChange, nodeId, setValue]);

  // Check for changes
  useEffect(() => {
    if (!configRecord) {
      setHasChanges(false);
      onHasChangesChange?.(false);
      return;
    }

    const fieldsDifferFromCurrent = configRecord.fields.some((field) => {
      const formValue = getValues(field.name);
      return normalizeValue(formValue) !== normalizeValue(field.currentValue);
    });

    setHasChanges(fieldsDifferFromCurrent);
    onHasChangesChange?.(fieldsDifferFromCurrent);
  }, [fields, configRecord, getValues, onHasChangesChange]);

  const handleRevertField = async (fieldName: string) => {
    if (!nodeId || !configRecord || !nodeLevel) {
      return;
    }

    setRevertingField(fieldName);
    pendingRevertFieldRef.current = fieldName;
    try {
      await onRevertField(nodeId, fieldName);
      const queryKey = configurationQueryKey({
        nodeId,
        level: nodeLevel,
        instanceId: instanceId || undefined,
        region: region || undefined,
        accountId: accountId || undefined,
      });
      queryClient.invalidateQueries({ queryKey, exact: true });
    } catch (error) {
      pendingRevertFieldRef.current = null;
      console.error('Failed to revert field:', error);
    } finally {
      setRevertingField(null);
    }
  };

  const handleRevertAll = async () => {
    if (!nodeId || !configRecord || !nodeLevel) {
      return;
    }

    setIsRevertingAll(true);
    pendingRevertAllRef.current = true;
    try {
      await onRevertAll(nodeId);
      const queryKey = configurationQueryKey({
        nodeId,
        level: nodeLevel,
        instanceId: instanceId || undefined,
        region: region || undefined,
        accountId: accountId || undefined,
      });
      queryClient.invalidateQueries({ queryKey, exact: true });
    } catch (error) {
      pendingRevertAllRef.current = false;
      setIsRevertingAll(false);
      console.error('Failed to revert all:', error);
    }
  };

  return {
    register,
    control,
    fields,
    errors,
    isValid,
    hasChanges,
    revertingField,
    isRevertingAll,
    handleSubmit,
    getValues,
    setError,
    reset,
    handleRevertField,
    handleRevertAll,
  };
}
